import { Component, OnInit, Input, NgZone, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import {
  DataListViewModelItem,
  DataListViewModelSlidingOption,
} from '../data-list/data-list-view-model';
import { NavParams, NavController, PopoverController } from '@ionic/angular';
import { IMSAction } from 'src/app/_model/menu';
import { DynFormsService } from 'src/app/_services/dyn-forms/logic/dyn-forms.service';
import { DynForm } from 'src/app/_model/dyn-form';
import { DatePipe } from '@angular/common';
import { NavigationOptions } from '@ionic/angular/common/providers/nav-controller';
import { DynFormData } from 'src/app/_model/dyn-form-data';
import { AlertService } from 'src/app/_services/alert/alert.service';
import { Servicejob } from 'src/app/_model/servicejob';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { JobSectionsService } from 'src/app/_services/job-section/logic/job-section.service';
import { ActionInterpreterService } from 'src/app/_services/action-interpreter/action-interpreter.service';
import { CloudDraftDeleteService } from 'src/app/_services/dyn-forms/cloud-draft-delete.service';
import { DynFormNameService } from 'src/app/_services/dyn-forms/logic/dyn-form-name/dyn-form-name.service';

/**
 * Component displaying a list of external files
 */
@Component({
  selector: 'app-popover-jobsection-instance-dyn-form',
  templateUrl: './popover-jobsection-instance-dyn-form.component.html',
  styleUrls: ['./popover-jobsection-instance-dyn-form.component.scss'],
})
export class PopoverJobSectionInstanceDynFormComponent implements OnInit {
  // Inputs

  // Public Member
  public viewModel: DataListViewModelItem<DynFormData>[] = [];
  public instanceSlidingOptions = new Map<
    string,
    DataListViewModelSlidingOption<DynFormData>
  >();

  public sjob: Servicejob;

  // Private Member

  // Lifecycle
  constructor(
    private dynFormNameService: DynFormNameService,
    private logger: LoggingService,
    private translate: TranslateService,
    private navParams: NavParams,
    private dynformsService: DynFormsService,
    private navigationController: NavController,
    private datePipe: DatePipe,
    private popoverCtrl: PopoverController,
    private alertService: AlertService,
    private jobSectionsService: JobSectionsService,
    private servicejobService: ServicejobsService,
    private clientState: ClientStateService,
    private actionInterpreter: ActionInterpreterService,
    private cloudDraftDelete: CloudDraftDeleteService,
  ) {}

  async ngOnInit() {
    const sjobId = this.clientState.theState.getValue().serviceJobId;
    this.sjob = this.servicejobService.getServiceJobById(sjobId);

    const params = this.navParams.data['params'];
    const defaultClickAction: IMSAction = JSON.parse(
      params['defaultClickAction'],
    );
    const dynForm: DynForm = this.navParams.data['dynForm'];

    let targetRef;

    if (defaultClickAction.params) {
      targetRef = defaultClickAction.params['targetRef'];
    }

    if (defaultClickAction && targetRef && dynForm) {
      this.logger.log(
        this.constructor.name,
        'Create JobSection Instance Popover for Dyn Form',
        defaultClickAction.target,
      );

      this.instanceSlidingOptions.set('delete', {
        color: 'danger',
        expandable: false,
        icon: 'trash',
        onClick: (formData) => this.confirmDelete(formData),
      });

      const title = this.translate.instant(
        'COMPONENT.POPOVER.JOBSECTION-MULTIINSTANCE-DYNFORM.CREATE-NEW',
      );
      let defaultOption = {
        id: undefined,
        title: title,
        click: async () => {
          // Initiate a new form
          await this.popoverCtrl.dismiss();
          await this.actionInterpreter.executeAction(defaultClickAction);
        },
      };

      try {
        let formDatas =
          await this.dynformsService.getNotFinishedJobSectionDynFormDataFor(
            dynForm,
            targetRef,
          );

        this.logger.log(
          this.constructor.name,
          'Found formDatas for jobsections',
          formDatas,
          'push them to the viewmodel',
          this.viewModel,
        );

        this.viewModel = formDatas
          .sort((a, b) => {
            let jobSectionA = this.jobSectionsService.getFullJobSectionLabel(
              this.jobSectionsService.getJobSectionById({
                imsId: +a.jobSectionId,
                secondaryId: undefined,
                objRef: 'ims/JobSection',
              }),
            );
            let jobSectionB = this.jobSectionsService.getFullJobSectionLabel(
              this.jobSectionsService.getJobSectionById({
                imsId: +b.jobSectionId,
                secondaryId: undefined,
                objRef: 'ims/JobSection',
              }),
            );
            return jobSectionA.localeCompare(jobSectionB);
          })
          .map((formData) => {
            let model: DataListViewModelItem<DynFormData> = {
              id: formData,
              title:
                (this.dynFormNameService.getFormName(dynForm) ?? dynForm.name) +
                ' (' +
                this.jobSectionsService.getFullJobSectionLabel(
                  this.jobSectionsService.getJobSectionById({
                    imsId: +formData.jobSectionId,
                    secondaryId: undefined,
                    objRef: 'ims/JobSection',
                  }),
                ) +
                ')',
              startSlidingOptions: ['delete'],
              click: async (formData) => {
                // Switch to JobSection
                this.clientState.setJobSectionId({
                  imsId: +formData.jobSectionId,
                  secondaryId: undefined,
                  objRef: 'ims/JobSection',
                });
                console.log('Execute default action with new jobsection');
                await this.popoverCtrl.dismiss();
                await this.actionInterpreter.executeAction(defaultClickAction);
              },
            };
            return model;
          });

        this.logger.log(
          this.constructor.name,
          'viewmodel after pushing formdatas',
          this.viewModel,
        );
      } catch (error) {
        this.logger.log(
          this.constructor.name,
          ' Error creating popover',
          error,
        );
      }
    } else {
      this.logger.error(
        this.constructor.name,
        ' cannot generate a jobsection instance popover without a dynform id',
      );
    }
  }

  // Public Functions

  // Private Functions
  /**
   * KRK-45: asks before deleting, then hands the entry to the CloudDraftDeleteService.
   *
   * The popover stays open, exactly like the draft list modal: dismissing it threw the user
   * back out to the menu after every single delete although they were working through their
   * entries. The alert is a later overlay than the popover, so Ionic stacks it on top - it
   * does not need the popover out of the way.
   */
  private confirmDelete(formData: DynFormData): void {
    const title = this.translate.instant(
      'COMPONENT.POPOVER.JOBSECTION-MULTIINSTANCE-DYNFORM.DELETE-FORM.TITLE',
    );
    // KRK-45: an entry that exists in the cloud is deleted everywhere, so say so
    const message = this.translate.instant(
      formData?.acid != undefined
        ? 'COMPONENT.POPOVER.JOBSECTION-MULTIINSTANCE-DYNFORM.DELETE-FORM.MESSAGE-CLOUD'
        : 'COMPONENT.POPOVER.JOBSECTION-MULTIINSTANCE-DYNFORM.DELETE-FORM.MESSAGE',
    );

    this.alertService.showAlert(
      title,
      undefined,
      message,
      title,
      () => {
        void this.deleteAndRemoveRow(formData);
      },
      undefined,
      undefined,
      true,
      'danger',
    );
  }

  /**
   * Deletes the entry and takes its row out of the open popover.
   *
   * Unlike the draft list modal this list is not reactive, so the row has to be dropped by
   * hand - and the array is replaced instead of spliced, because the data list receives its
   * items through a setter and would not notice an in-place mutation.
   *
   * This list has no "create new" entry, so the last deleted row leaves nothing behind and
   * the popover closes itself.
   */
  private async deleteAndRemoveRow(formData: DynFormData): Promise<void> {
    try {
      await this.cloudDraftDelete.deleteDraft(formData);
    } catch (error) {
      // The row stays - it is still there as far as we know
      this.logger.error(
        this.constructor.name,
        'Error deleting form entry - formDataId',
        formData?.formDataId,
        'acid',
        formData?.acid,
        error?.message,
      );
      return;
    }

    this.viewModel = this.viewModel.filter((item) => item.id !== formData);
    if (this.viewModel.length === 0) {
      void this.popoverCtrl.dismiss();
    }
  }

  private navigateToForm(
    dynFormId: number,
    targetRef: string,
    formData: DynFormData,
  ) {
    let navParams: NavigationOptions = {
      queryParams: {
        dynFormId: dynFormId,
        targetRef: targetRef,
        formData: JSON.stringify(formData),
      },
    };
    this.popoverCtrl.dismiss();
    this.navigationController.navigateForward('/feature/dyn-form', navParams);
  }
}
